import React, { useState, useEffect } from 'react';

import MUIDataTable, { debounceSearchRender } from 'mui-datatables';
import { styled, useTheme } from '@mui/material/styles';

import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import CapTableFooter from '../../pages/issuer/Dashboard/CapTableFooter';

const StyledTable = styled(MUIDataTable)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    '&.MuiPaper-root': {
        backgroundColor: theme.palette.background.paper,
        boxShadow:
            theme.palette.mode === 'dark'
                ? '0 0 20px rgba(0,0,0,0.4)'
                : '0 0 20px rgba(0,0,0,0.2)',
        borderRadius: '15px !important',
    },
    MUIDataTableHeadCell: {
        toolButton: {
            justifyContent: 'center',
        },
    },
}));

const LoadingComponent = (props) => {
    return (
        <div
            style={{
                position: 'absolute',
                zIndex: 1110,
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: 15,
                background: props.isDark
                    ? 'rgba(0,0,0,0.4)'
                    : 'rgba(255,255,255,0.8)',
            }}
        >
            <CircularProgress size={24} />
        </div>
    );
};

/**
 * Custom DataTable component
 *
 * The table can either be server-side, when the prop isServerSide={true},
 * or client-side, of no "isServerSide" prop is provided.
 * If the table is server-side, the "data" prop should be a method returning the data, with pagination.
 * If the table is client-side, the "data" prop is the the actual data array.
 */
const DataTable = ({
    data,
    columns,
    title,
    refresh,
    isServerSide,
    onDeleteRows,
    shareSort = false,
    expandable = null,
    fontSize = null,
    setDisplayCategoryColumns,
    capTableFooterData,
    capTableFooter = false,
    updatePreferredColums,
    customToolbar = null,
    disableServerSideControls = false,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    // const [sortOrder, setSortOrder] = useState({})
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [count, setCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);

    const [tableData, setTableData] = useState(null);
    useEffect(() => {
        if (isServerSide) {
            setLoading(true);
            data({ page: 1, limit: 10 }) // defaults
                .then((res) => {
                    setTableData(res.data.data);
                    setCount(res.data.total);
                })
                .catch((error) => {})
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setTableData(data);
        }
    }, [data, isServerSide]);

    const changePage = (newPage) => {
        setLoading(true);
        data({
            page: newPage + 1,
            limit: rowsPerPage,
            search: searchTerm,
        })
            .then((res) => {
                setTableData(res.data.data);
                setCount(res.data.total);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const changeRowsPerPage = (newRows) => {
        setLoading(true);
        data({
            page: 1,
            limit: newRows,
            search: searchTerm,
        })
            .then((res) => {
                setRowsPerPage(newRows);
                setTableData(res.data.data);
                setCount(res.data.total);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleFilterSubmit = (applyFilters) => {
        let filters = {};
        let filterList = applyFilters();
        for (let i = 0; i < filterList.length; i++) {
            if (filterList[i].length) {
                filters[columns[i].name] = filterList[i][0];
            }
        }
        setLoading(true);
        data({
            page: 1,
            limit: rowsPerPage,
            search: searchTerm,
            ...filters,
        })
            .then((res) => {
                setTableData(res.data.data);
                setCount(res.data.total);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleSearch = (searchTerm) => {
        setLoading(true);
        data({
            page: 1,
            limit: rowsPerPage,
            search: searchTerm,
        })
            .then((res) => {
                setTableData(res.data.data);
                setSearchTerm(searchTerm);
                setCount(res.data.total);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const options = {
        serverSide: true,
        filter: !disableServerSideControls,
        search: !disableServerSideControls,
        selectableRows: disableServerSideControls ? 'none' : undefined,
        // Explicit null (not undefined) keeps mui-datatables' reserved
        // leftmost select-cell column rendered as empty space instead of
        // omitting it entirely, matching the client-side tables' look.
        expandableRows: null,
        textLabels: {
            body: {
                noMatch: 'No records found',
            },
        },
        filterType: 'dropdown',
        responsive: 'vertical',
        count: count,
        rowsPerPage: rowsPerPage,
        rowsPerPageOptions: [5, 10, 20, 50, 100, 200],
        onRowsDelete: (lookup) => {
            let rows = [];
            for (var i in lookup.data) {
                rows.push(tableData[lookup.data[i].dataIndex].id);
            }
            onDeleteRows(rows);
        },
        // sortOrder: sortOrder,
        onSearchChange: (searchText) => {
            handleSearch(searchText);
        },
        customSearchRender: debounceSearchRender(500),
        confirmFilters: true,
        onTableChange: (action, tableState) => {
            switch (action) {
                case 'changePage':
                    changePage(tableState.page);
                    break;
                case 'changeRowsPerPage':
                    changeRowsPerPage(tableState.rowsPerPage);
                    break;
                default:
                //
            }
        },
        customFilterDialogFooter: (currentFilterList, applyNewFilters) => {
            return (
                <div style={{ marginTop: '40px' }}>
                    <Button
                        variant="contained"
                        onClick={() => handleFilterSubmit(applyNewFilters)}
                    >
                        Apply Filters
                    </Button>
                </div>
            );
        },
        onFilterChange: (column, filterList, type) => {
            if (type === 'chip') {
                var newFilters = () => filterList;
                handleFilterSubmit(newFilters);
            }
        },
    };

    const handleRowExpansionChange = (expandedRows) => {
        if (setDisplayCategoryColumns) {
            setDisplayCategoryColumns(expandedRows.data.length > 0);
        }
    };

    return (
        <React.Fragment>
            {tableData ? (
                <div style={{ width: '100%' }}>
                    {(refresh || loading) && (
                        <LoadingComponent isDark={isDark} />
                    )}
                    <StyledTable
                        title={title}
                        data={tableData}
                        columns={columns}
                        options={
                            isServerSide
                                ? { ...options, customToolbar }
                                : {
                                      customToolbar,
                                      expandableRows: !expandable
                                          ? null
                                          : expandable.expandableRows,
                                      expandableRowsHeader: !expandable
                                          ? null
                                          : expandable.expandableRowsHeader,
                                      expandableRowsOnClick: !expandable
                                          ? null
                                          : expandable.expandableRowsOnClick,
                                      renderExpandableRow: !expandable
                                          ? null
                                          : expandable.renderExpandableRow,
                                      rowsExpanded: !expandable ? null : null, //[...Array(tableData.length).keys()],
                                      selectableRows: 'none',
                                      rowHover: true,
                                      textLabels: {
                                          body: {
                                              noMatch:
                                                  tableData &&
                                                  tableData.length === 0 &&
                                                  'No records found',
                                          },
                                      },
                                      onTableChange: (action, tableState) => {
                                          switch (action) {
                                            case 'rowExpansionChange':
                                                if (setDisplayCategoryColumns)
                                                    handleRowExpansionChange(
                                                        tableState.expandedRows
                                                      );
                                                break;
                                            case 'viewColumnsChange':
                                                updatePreferredColums(tableState.columns);
                                                break;
                                            default:
                                            //
                                          }
                                      },
                                      customSort: shareSort
                                          ? (data, colIndex, order) => {
                                                return data.sort((a, b) => {
                                                    if (
                                                        colIndex === shareSort
                                                    ) {
                                                        // sort for numbers with "thousand separators"
                                                        var aVal;
                                                        if (a.data[colIndex]) {
                                                            aVal = a.data[
                                                                colIndex
                                                            ].replace(/,/g, '');
                                                            aVal = parseFloat(
                                                                aVal,
                                                                2
                                                            );
                                                        } else {
                                                            aVal = 0;
                                                        }
                                                        var bVal;
                                                        if (b.data[colIndex]) {
                                                            bVal = b.data[
                                                                colIndex
                                                            ].replace(/,/g, '');
                                                            bVal = parseFloat(
                                                                bVal,
                                                                2
                                                            );
                                                        } else {
                                                            bVal = 0;
                                                        }
                                                        if (aVal === bVal)
                                                            return 0;
                                                        return (
                                                            (aVal < bVal
                                                                ? -1
                                                                : 1) *
                                                            (order === 'desc'
                                                                ? 1
                                                                : -1)
                                                        );
                                                    } else {
                                                        // sorting for all other columns
                                                        if (
                                                            a.data[colIndex] ===
                                                            b.data[colIndex]
                                                        )
                                                            return 0;
                                                        return (
                                                            (a.data[colIndex] <
                                                            b.data[colIndex]
                                                                ? -1
                                                                : 1) *
                                                            (order === 'desc'
                                                                ? 1
                                                                : -1)
                                                        );
                                                    }
                                                });
                                            }
                                          : null,
                                       onDownload: (buildHead, buildBody, columns, data) => {
                                        const newColumns = [
                                            {
                                                name: 'Class',
                                                download: true
                                            },
                                            {
                                                name: 'Investor',
                                                download: true
                                            },
                                            {
                                                name: 'Shares',
                                                download: true
                                            },
                                            {
                                                name: 'Shares %',
                                                download: true
                                            },
                                            {
                                                name: 'Price',
                                                download: true
                                            },
                                            {
                                                name: 'Acquired Price',
                                                download: true
                                            },
                                            {
                                                name: 'Acquired Date',
                                                download: true
                                            }
                                        ];
                                        const newData = [];

                                        data.forEach(row => {
                                            const investors = row.data[8];
                                            investors.forEach(investor => {
                                                const newRow = [row.data[0], investor.investor, investor.shares, investor['shares_%'], row.data[3], investor.acquired_price, investor.acquired_date];
                                                newData.push(newRow);
                                            });
                                        });
                                        
                                        return buildHead(newColumns) + buildBody(newData.map((row, index) => ({
                                            index,
                                            data: row
                                        })));
                                       },
                                      customFooter: capTableFooter
                                          ? (
                                                count,
                                                page,
                                                rowsPerPage,
                                                changeRowsPerPage,
                                                changePage
                                            ) => {
                                                return (
                                                    <CapTableFooter
                                                        capTableFooterData={
                                                            capTableFooterData
                                                                ? capTableFooterData
                                                                : null
                                                        }
                                                    />
                                                );
                                            }
                                          : null,
                                  }
                        }
                    />
                </div>
            ) : (
                <Grid
                    item
                    xs={12}
                    style={{ display: 'flex', justifyContent: 'center' }}
                >
                    <CircularProgress />
                </Grid>
            )}
        </React.Fragment>
    );
};

export default DataTable;
